// src/drivers/cytoscape-driver.js
//
// Wraps Cytoscape.js. Never `import cytoscape from 'cytoscape'` here — that
// would make this file, and therefore the whole library, drag in a
// dependency most consumers did not ask for (a page using the vis-network
// or force-graph driver has no reason to ship Cytoscape too). Instead the
// real `cytoscape` factory function is injected by whoever constructs this
// driver: `import cytoscape from 'cytoscape'` in a bundler, or
// `window.cytoscape` off a CDN `<script>` tag in a plain Alpine page —
// either way, this file never chooses.

import { GraphDriver } from '../graph-driver.js';

/** A minimal, real default — not empty, but never assumed to be someone's final style. */
function default_style() {
    return [
        { selector: 'node', style: {
            'background-color': '#4b6bfb',
            label: 'data(label)',
            color: '#1a1a1a',
            'font-size': 10,
            'text-valign': 'bottom',
            'text-margin-y': 4,
            width: 24,
            height: 24,
        } },
        { selector: 'edge', style: {
            width: 1,
            'line-color': '#c7c7c7',
            'target-arrow-color': '#c7c7c7',
            'target-arrow-shape': 'none',
            'curve-style': 'haystack',
        } },
    ];
}

export class CytoscapeDriver extends GraphDriver {
    /** @param {(options: object) => object} cytoscape_factory the `cytoscape` function itself */
    constructor(cytoscape_factory) {
        super();

        if (typeof cytoscape_factory !== 'function') {
            throw new Error('CytoscapeDriver needs the real cytoscape factory function — none was given');
        }

        this._cytoscape = cytoscape_factory;
        this._cy = null;
        this._layout_c = 'cose';
        this._listeners = { click: [], hover: [], select: [], drag: [] };
        this._drag_origin = new Map();
    }

    mount(container, options = {}) {
        this._layout_c = options.layout_c ?? this._layout_c;

        this._cy = this._cytoscape({
            container,
            elements: [],
            style: options.style ?? default_style(),
            layout: { name: this._layout_c },
            ...(options.cytoscape_options ?? {}),
        });

        this._cy.on('tap', 'node', evt => this._emit('click', { type: 'node', id: evt.target.id(), data: evt.target.data() }));
        this._cy.on('tap', 'edge', evt => this._emit('click', { type: 'edge', id: evt.target.id(), data: evt.target.data() }));
        this._cy.on('mouseover', 'node', evt => this._emit('hover', { type: 'node', id: evt.target.id(), data: evt.target.data() }));
        this._cy.on('select', 'node', evt => this._emit('select', { type: 'node', id: evt.target.id(), data: evt.target.data() }));

        // 'grab' marks where a drag started (so the first 'drag' tick has
        // something to diff against), 'drag' fires continuously with the
        // delta since the previous tick — not since the start of the drag,
        // which is what a listener moving other nodes by the same amount
        // needs — and 'free' (drag end, any drag end, not just a
        // successful one) clears the origin so a stale entry can never
        // leak into a later, unrelated drag of the same node.
        this._cy.on('grab', 'node', evt => {
            this._drag_origin.set(evt.target.id(), { ...evt.target.position() });
        });
        this._cy.on('drag', 'node', evt => {
            const id = evt.target.id();
            const now = evt.target.position();
            const prev = this._drag_origin.get(id) ?? now;
            const delta = { dx: now.x - prev.x, dy: now.y - prev.y };

            this._drag_origin.set(id, { ...now });
            this._emit('drag', { type: 'node', id, data: evt.target.data(), position: { ...now }, delta });
        });
        this._cy.on('free', 'node', evt => {
            this._drag_origin.delete(evt.target.id());
        });
    }

    setData(nodes, edges) {
        // cose (and most of cytoscape's other real layouts) animate
        // continuously by default (`animate: true` in cytoscape's own
        // layout defaults) — a call here while an earlier layout is still
        // mid-animation, rather than replacing it cleanly, was found to
        // leave the old one's animation frame still scheduled, which then
        // fires against elements this call is about to remove. stop()
        // cancels any running layout/animation outright before this one
        // starts.
        this._cy.stop();
        this._cy.elements().remove();

        this._cy.add([
            ...nodes.map(node => ({ data: { label: node.id, ...node } })),
            ...edges.map(edge => ({ data: { id: `${edge.source}→${edge.target}`, ...edge } })),
        ]);

        this._cy.layout({ name: this._layout_c }).run();
    }

    on(event, callback) {
        if (!(event in this._listeners)) {
            throw new Error(`CytoscapeDriver has no "${event}" event — use click, hover, select, or drag`);
        }

        this._listeners[event].push(callback);
    }

    _emit(event, payload) {
        for (const callback of this._listeners[event]) {
            callback(payload);
        }
    }

    moveNode(id, dx, dy) {
        const ele = this._cy.getElementById(id);

        if (ele.empty()) {
            return;
        }

        // shift() is relative by construction (cytoscape >= 3.19); the
        // position()-getter-then-setter fallback exists only for an older
        // pinned version somewhere doing the exact same thing by hand.
        if (typeof ele.shift === 'function') {
            ele.shift({ x: dx, y: dy });
        } else {
            const pos = ele.position();
            ele.position({ x: pos.x + dx, y: pos.y + dy });
        }
    }

    fit() {
        this._cy.fit();
    }

    resize() {
        this._cy.resize();
        this._cy.fit();
    }

    destroy() {
        // The real bug this stop() fixes, found live (not in a test): cose's
        // own animation was still running (see setData()'s own comment on
        // why that is the default, not an edge case) when destroy() ran —
        // its next scheduled animation frame then fired against internal
        // state destroy() had already torn down, throwing from inside
        // cytoscape's own minified code ("can't access property 'notify',
        // t is null") well after control had returned to this driver's
        // caller. stop() cancels that in-flight animation outright first.
        this._cy.stop();
        this._cy.destroy();
        this._cy = null;
    }
}
